import { Injectable } from '@angular/core'

import { PageInfo } from '../../common'
import { ADMIN_API_ENDPOINTS } from '../../common/api-endpoints'
import {
  ExportStampHistoryDto,
  FindStampHistoryDto,
  MilestoneDynamicQrDto,
  MilestoneQrDto,
  PageResultDto,
  StampHistoryItemDto,
  StampHistorySummaryDto,
  localizedToTranslation
} from '../../dto'
import { HttpService } from '../http.service'

import {
  AdminChallengeMilestoneIndexService,
  MilestoneIndexEntry
} from './admin-challenge-milestone-index.service'

/**
 * API #21 phân trang như API #22 (`limit` mặc định **10**, tối đa 100 theo Swagger), trong khi
 * `milestoneLimit` cho phép tới `CHALLENGE_CONFIG_VALUE_MAX`. Thiếu dòng tổng hợp không ra
 * bảng rỗng mà bị `mergeSummaryWithMilestones()` hiểu nhầm thành **0 tem** — sai số liệu.
 *
 * `getAll` là quy ước chung của BE cho endpoint cần lấy trọn danh sách (`/events`,
 * `/tenants`, `/offline-tenants`...). Swagger #21 chưa khai param này nên vẫn gửi kèm
 * `limit` làm cận dưới: BE hỗ trợ `getAll` thì lấy đủ, không hỗ trợ thì vẫn được 100 dòng.
 */
const SUMMARY_PAGE_LIMIT = 100

/** Kết quả export qua email — BE gửi file về email người dùng (giống Order Management). */
export interface StampHistoryExportResponseDto {
  success?: boolean
  message?: string
  jobId?: string
}

/** Thời hạn QR động mặc định khi không tính được từ `expiresAt` (FR-03-BR-06 ~60s). */
const DYNAMIC_QR_FALLBACK_SECONDS = 60

/**
 * FR-06 — Quản lý Stamp History + QR của Milestone.
 *
 * ✅ Swagger đủ 23/23 API xác nhận 2026-08-25 — schema #21, #15 lệch so với `API Design.md`
 * ban đầu, xem `docs/stamp-collection/SWAGGER-DIFF-2.md` §1.6-1.7 trước khi sửa file này.
 *   #21 GET  /stamp-collection/challenge-stamps/summary  → summary()
 *   #22 GET  /stamp-collection/challenge-stamps          → list()
 *   #23 GET  /stamp-collection/challenge-stamps/export    → downloadExcel()
 *   #15 GET  /stamp-collection/challenges/:cid/milestones/:mid/qr-code
 *                                                        → getMilestoneQr()
 *   #16 POST /stamp-collection/challenges/:cid/milestones/:mid/mall-shopping-qr
 *                                                        → createDynamicQr()
 */
@Injectable({
  providedIn: 'root'
})
export class AdminStampHistoryService {

  constructor(
    private httpService: HttpService,
    private milestoneIndexService: AdminChallengeMilestoneIndexService
  ) {}

  /**
   * API #21 — FR-06-BR-01: tổng hợp số tem theo từng Milestone.
   *
   * ⚠️ Summary nhận cả bộ filter của API #22 ⇒ không còn là tổng cố định. Hàm này nhận thêm
   * `params` optional để giữ parity khi UI bổ sung filter.
   *
   * ✅ Swagger 2026-08-25: response có sẵn `milestonePosition` + `milestoneType` ngay trong
   * mỗi dòng, nên không cần join API #10 chỉ để lấy hai field đó nữa.
   *
   * ⚠️ NHƯNG response **chỉ có dòng cho Milestone đã phát tem** (BE xác nhận 2026-09-15:
   * *"nếu summary không có dòng tương ứng thì hiểu stampCount = 0"*) ⇒ vẫn phải ghép với
   * danh sách Milestone đầy đủ, xem `mergeSummaryWithMilestones()`.
   *
   * ✅ ĐÃ CHỐT: không truyền `resultStatus` (mặc định) ⇒ BE chỉ trả tem `ISSUED`. Khái niệm
   * tem lỗi (`failedCount`) đã bỏ khỏi UI Stamp History, không gọi thêm request phụ.
   */
  public async summary(
    challengeId: string,
    params?: FindStampHistoryDto
  ): Promise<StampHistorySummaryDto[]> {
    const queryParams: any = { getAll: true, limit: SUMMARY_PAGE_LIMIT }
    if (challengeId) queryParams.challengeId = challengeId
    this.applyStampFilters(queryParams, params)

    const rows = await this.httpService.get<any[]>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE_STAMP}/summary`,
      queryParams
    )

    return this.mergeSummaryWithMilestones(challengeId, rows || [])
  }

  /**
   * Bảng tổng hợp phải liệt kê **mọi** Milestone của Challenge, kể cả cái chưa phát tem nào.
   *
   * API #21 bỏ qua những Milestone đó, mà `stamp-history.component.html` lại dùng chung mảng
   * này cho cả bộ lọc Milestone và nút View QR — render thẳng response thì Challenge vừa tạo
   * sẽ có tab Stamp History trắng trơn, không mở được QR để in mã dán tại Mall.
   *
   * Danh sách gốc lấy từ API #10 qua `AdminChallengeMilestoneIndexService` (cache theo
   * `challengeId`, `list()` cũng dùng chung) nên không phát sinh thêm request.
   */
  private async mergeSummaryWithMilestones(
    challengeId: string,
    rows: any[]
  ): Promise<StampHistorySummaryDto[]> {
    const milestoneIndex = await this.milestoneIndexService.get(challengeId)
    const rowByMilestoneId = new Map<string, any>()

    rows.forEach(row => {
      if (!row?.milestoneId) {
        return
      }

      rowByMilestoneId.set(row.milestoneId, row)
    })

    const summaries = Array.from(milestoneIndex.values())
      .sort((first, second) => (first.position || 0) - (second.position || 0))
      .map(milestone => {
        const row = rowByMilestoneId.get(milestone.id)
        rowByMilestoneId.delete(milestone.id)

        return this.toSummaryDto(row, challengeId, milestone)
      })

    // Dòng không khớp Milestone nào của API #10 vẫn giữ lại thay vì nuốt mất dữ liệu BE trả
    // về. API #10 lỗi ⇒ index rỗng ⇒ nhánh này trả nguyên response, y như trước khi ghép.
    rowByMilestoneId.forEach(row => summaries.push(this.toSummaryDto(row, challengeId)))

    return summaries
  }

  /** `milestone` có giá trị thì API #10 là nguồn chuẩn cho tên / position / type. */
  private toSummaryDto(
    row: any,
    challengeId: string,
    milestone?: MilestoneIndexEntry
  ): StampHistorySummaryDto {
    return StampHistorySummaryDto.create({
      challengeId: row?.challengeId || challengeId,
      challengeName: row?.challengeName,
      challengeMilestoneId: milestone?.id || row?.milestoneId,
      // API #10 trả TranslationDto thật; #21 chỉ có string đã localize nên phải bọc lại.
      milestoneName: milestone?.name || localizedToTranslation(row?.milestoneName),
      milestonePosition: milestone?.position ?? row?.milestonePosition,
      milestoneType: milestone?.type || row?.milestoneType,
      // API #10 là nguồn duy nhất cho Milestone chưa phát tem (API #21 không có dòng). BE
      // chưa ship field này thì `undefined` ⇒ rơi về giá trị của API #21 như trước.
      qrAvailable: milestone?.qrAvailable ?? row?.qrAvailable,
      eventId: milestone?.eventId,
      // Không có dòng trong API #21 nghĩa là Milestone chưa phát tem nào.
      issuedStamps: row?.stampCount || 0
    })
  }

  /**
   * API #22 — FR-06-BR-02/03: chi tiết tem, lọc theo Milestone và khoảng ngày.
   *
   * ✅ ĐÃ CHỐT: không truyền `resultStatus` ⇒ BE mặc định chỉ trả tem `ISSUED`, không cần
   * component tự set `resultStatus=ISSUED` — hành vi mặc định của BE đã đúng ý UI.
   */
  public async list(
    params: FindStampHistoryDto
  ): Promise<{ rows: StampHistoryItemDto[]; count: number; pageInfo: PageInfo }> {
    const page = params.page || 1
    const pageSize = params.pageSize || 10

    const queryParams: any = {
      limit: pageSize,
      offset: (page - 1) * pageSize
    }
    if (params.challengeId) queryParams.challengeId = params.challengeId
    this.applyStampFilters(queryParams, params)

    const result = await this.httpService.get<PageResultDto<any>>(
      ADMIN_API_ENDPOINTS.CHALLENGE_STAMP,
      queryParams
    )

    const milestoneIndex = await this.milestoneIndexService.get(params.challengeId)

    const rows = (result?.rows || []).map(row =>
      StampHistoryItemDto.create({
        id: row?.stampId,
        challengeId: row?.challengeId,
        challengeName: row?.challengeName,
        customerId: row?.customerId,
        customerName: row?.customerName,
        phoneNumber: row?.customerPhoneNumber,
        challengeMilestoneId: row?.milestoneId,
        milestoneName: localizedToTranslation(row?.milestoneName),
        // ⚠️ BE-MISSING: join từ API #10 để dựng mã MS-00x
        milestonePosition: milestoneIndex.get(row?.milestoneId)?.position,
        sourceType: row?.sourceType,
        resultStatus: row?.resultStatus,
        failureCode: row?.failureCode,
        mallId: row?.mallId,
        mallName: localizedToTranslation(row?.mallName),
        offlineTenantId: row?.offlineTenantId,
        offlineTenantName: localizedToTranslation(row?.offlineTenantName),
        orderNumber: row?.orderNumber,
        transactionAmount: row?.transactionAmount,
        attemptedAt: row?.attemptedAt,
        issuedAt: row?.issuedAt
        // ⚠️ BE-MISSING N5: `stampCode` không có trong response.
      })
    )

    const pageInfo = PageInfo.create()
    pageInfo.page = page
    pageInfo.pageSize = pageSize
    pageInfo.collectionSize = result.count

    return { rows, count: result.count, pageInfo }
  }

  /**
   * FR-11-BR-01 — Export issued stamp report: gửi file .xlsx về email đăng ký của user,
   * cùng luồng với các báo cáo khác của màn Report. Body mirror API #7 của Reward History.
   *
   * ⚠️ CHỜ BE: `POST /challenge-stamps/export-excel` đã probe 2026-08-25 trả **404**
   * ("Cannot POST ..."). FE nối sẵn theo quyết định 2026-09-10 — BE bổ sung endpoint sau,
   * tới lúc đó card "Issued stamp report" mới chạy được.
   */
  public async exportExcelByEmail(
    payload: ExportStampHistoryDto
  ): Promise<StampHistoryExportResponseDto> {
    return await this.httpService.post<StampHistoryExportResponseDto>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE_STAMP}/export-excel`,
      {
        email: payload.email,
        challengeId: payload.challengeId,
        milestoneId: payload.milestoneId,
        search: payload.search,
        from: payload.startDate ? new Date(payload.startDate).toISOString() : undefined,
        to: payload.endDate ? new Date(payload.endDate).toISOString() : undefined
      }
    )
  }

  /**
   * API #23 — tải trực tiếp file XLSX (binary), cùng filter và actor scope với API #22.
   * Dùng được ngay, không phụ thuộc B4.
   */
  public async downloadExcel(payload: ExportStampHistoryDto): Promise<Blob> {
    const queryParams: any = {}
    if (payload.challengeId) queryParams.challengeId = payload.challengeId
    if (payload.milestoneId) queryParams.milestoneId = payload.milestoneId
    if (payload.search) queryParams.search = payload.search
    if (payload.startDate) queryParams.from = new Date(payload.startDate).toISOString()
    if (payload.endDate) queryParams.to = new Date(payload.endDate).toISOString()
    if (payload.sourceType) queryParams.sourceType = payload.sourceType
    if (payload.resultStatus) queryParams.resultStatus = payload.resultStatus
    if (payload.failureCode) queryParams.failureCode = payload.failureCode

    return await this.httpService.downloadBlob<Blob>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE_STAMP}/export`,
      queryParams
    )
  }

  /**
   * API #15 — QR **tĩnh** của Milestone `VISIT_MALL` / `CHECK_IN_EVENT` (FR-06-BR-05).
   *
   * Trước đây FE đọc `StampHistorySummaryDto.qrCode` trực tiếp từ API summary; API #21 nay
   * chỉ trả `qrAvailable: boolean` nên bắt buộc gọi endpoint riêng này.
   *
   * ✅ Swagger 2026-08-25 — GIẢI QUYẾT DỨT ĐIỂM thắc mắc `qrCode` vs `qrPayload`: response
   * thật là `{challengeId, milestoneId, milestoneType, qrPayload}`, **không còn `qrCode`**.
   * Chỉ dùng `qrPayload`.
   */
  public async getMilestoneQr(
    challengeId: string,
    milestoneId: string
  ): Promise<MilestoneQrDto> {
    const raw = await this.httpService.get<any>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE}/${challengeId}/milestones/${milestoneId}/qr-code`
    )

    return MilestoneQrDto.create({ qrPayload: raw?.qrPayload })
  }

  /**
   * API #16 — QR **động** cho Milestone Mall shopping (FR-03-BR-06):
   * hết hạn ~60s, dùng 1 lần, server ký. BE ghi rõ chỉ sinh *sau khi admin xác minh
   * hoá đơn thủ công*.
   *
   * ⚠️ BE trả `qrToken` (không phải `qrCode`) và **không** trả `expiresInSeconds`
   * → map tên + tự tính countdown từ `expiresAt`.
   */
  public async createDynamicQr(
    challengeId: string,
    milestoneId: string
  ): Promise<MilestoneDynamicQrDto> {
    const raw = await this.httpService.post<any>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE}/${challengeId}/milestones/${milestoneId}/mall-shopping-qr`
    )

    return MilestoneDynamicQrDto.create({
      qrCode: raw?.qrToken,
      expiresAt: raw?.expiresAt,
      expiresInSeconds: this.toRemainingSeconds(raw?.expiresAt)
    })
  }

  // ===============================================================================================
  // Helpers
  // ===============================================================================================

  /** Query dùng chung cho API #21, #22, #23. */
  private applyStampFilters(queryParams: any, params?: FindStampHistoryDto): void {
    if (!params) {
      return
    }

    if (params.milestoneId) queryParams.milestoneId = params.milestoneId
    if (params.search) queryParams.search = params.search
    // ⚠️ BE đổi tên: fromDate/toDate → from/to
    if (params.fromDate) queryParams.from = params.fromDate
    if (params.toDate) queryParams.to = params.toDate
    if (params.sourceType) queryParams.sourceType = params.sourceType
    if (params.resultStatus) queryParams.resultStatus = params.resultStatus
    // ⚠️ Swagger 2026-08-25: #21/#22/#23 KHÔNG còn nhận `failureCode` làm filter (chỉ còn là
    // field hiển thị trên mỗi dòng). Field giữ trong `FindStampHistoryDto` cho UI tương lai,
    // không gửi lên BE nữa.
  }

  private toRemainingSeconds(expiresAt?: string): number {
    if (!expiresAt) {
      return DYNAMIC_QR_FALLBACK_SECONDS
    }

    const remaining = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000)

    return remaining > 0 ? remaining : 0
  }
}
